/**
 * The "Subscription" tab: what the account is currently entitled to, and the button that changes
 * it.
 *
 * `CurrentPeriodEnd` is the only lapse signal the backend acts on, which is why the term is spelled
 * out in words underneath the plan rather than left as a date to interpret — a grant with the wrong
 * period end looks identical to a correct one until the day it silently does or does not expire.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Edit, WorkspacePremium } from '@mui/icons-material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import { plansApi } from '@api/endpoints/plans'
import type { AdminUserDto } from '@api/types/admin'
import ManageSubscriptionDialog from '../ManageSubscriptionDialog'
import { planQueryKey } from '../plans/planSpecs'
import { adminUserSubscriptionKey } from './userQueryKeys'
import { useIsAdmin } from './useIsAdmin'
import { UserEntitlementsPanel } from './UserEntitlementsPanel'
import { allowanceFraction, allowanceLabel } from '../narration/narrationUsage'
import { describeTerm, errorMessage, subscriptionStatusName, userLabel } from './userFormatting'

export interface UserSubscriptionPanelProps {
  user: AdminUserDto
}

export function UserSubscriptionPanel({ user }: UserSubscriptionPanelProps) {
  const queryClient = useQueryClient()
  // Moderators see the plan read-only; changing it is Admin-only.
  const isAdmin = useIsAdmin()
  const [manageOpen, setManageOpen] = useState(false)

  const {
    data: subscription,
    isLoading,
    error,
  } = useQuery({
    queryKey: adminUserSubscriptionKey(user.id),
    queryFn: () => subscriptionsApi.getUserSubscription(user.id),
  })

  // The AI narration allowance lives on the plan, which the subscription DTO only summarises, so
  // read it from the Plans list (shared cache with the Plans page). Plans are Admin-only on the API.
  const plans = useQuery({
    queryKey: planQueryKey('consumer'),
    queryFn: plansApi.consumer.list,
    enabled: isAdmin && Boolean(subscription?.subscription),
    staleTime: 60_000,
  })
  const planId = subscription?.subscription?.plan.id
  const plan = plans.data?.find((p) => p.id === planId)

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkspacePremium fontSize="small" /> Subscription
          </Typography>
          {isAdmin && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => setManageOpen(true)}
              disabled={isLoading}
            >
              Change
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(error, 'Failed to load subscription')}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={20} />
        ) : !subscription?.subscription ? (
          <Typography variant="body2" color="text.secondary">
            No subscription record — treated as free tier
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">{subscription.subscription.plan.displayName}</Typography>
              <Chip
                label={subscriptionStatusName(subscription.subscription.status)}
                size="small"
                color={subscription.subscription.isActive ? 'success' : 'default'}
                variant="outlined"
              />
              {!subscription.subscription.autoRenew && !subscription.subscription.plan.isFreeTier && (
                <Chip label="No auto-renew" size="small" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {describeTerm(
                subscription.subscription.currentPeriodEnd,
                subscription.subscription.plan.isFreeTier
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {subscription.hasTripPlanner && <Chip label="Trip planner" size="small" />}
              {subscription.hasGroupRecording && <Chip label="Group recording" size="small" />}
              {subscription.hasGhostRacers && <Chip label="Ghost racers" size="small" />}
              {subscription.hasAiNarration && <Chip label="AI narration" size="small" />}
            </Box>
            {plan && (
              <Typography variant="caption" color="text.secondary">
                AI narration on this plan: {plan.hasAiNarration ? 'on' : 'off'},{' '}
                {plan.aiNarrationCharactersPerMonth.toLocaleString()} characters per month
                {plan.hasAiNarration && plan.aiNarrationCharactersPerMonth === 0 ? ' (none, so it can\'t be used)' : ''}
                {subscription.hasAiNarration || !plan.hasAiNarration ? '' : ' (not granted: the subscription is not active)'}
              </Typography>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                AI narration this month:{' '}
                {allowanceLabel(subscription.aiNarrationCharactersUsedThisMonth ?? 0, subscription.aiNarrationCharactersPerMonth)}
              </Typography>
              {(() => {
                const fraction = allowanceFraction(
                  subscription.aiNarrationCharactersUsedThisMonth ?? 0,
                  subscription.aiNarrationCharactersPerMonth
                )
                return fraction == null ? null : (
                  <LinearProgress
                    variant="determinate"
                    value={fraction}
                    color={fraction >= 90 ? 'warning' : 'primary'}
                    sx={{ height: 4, borderRadius: 2, mt: 0.25, maxWidth: 240 }}
                    aria-label="AI narration characters used this month"
                  />
                )
              })()}
            </Box>
          </Stack>
        )}
      </Paper>

      <UserEntitlementsPanel userId={user.id} />

      {isAdmin && (
      <ManageSubscriptionDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        userId={user.id}
        userLabel={userLabel(user)}
        current={subscription ?? null}
        // The dialog hands back the subscription it just wrote, so seed the cache with it rather
        // than refetching a value we already hold.
        onSuccess={(updated) => queryClient.setQueryData(adminUserSubscriptionKey(user.id), updated)}
      />
      )}
    </Box>
  )
}
